import { ref, watch } from "vue";
import { useI18n } from "vue-i18n";

export interface ChatMessage {
  id: string;
  role: "user" | "assistant";
  content: string;
  timestamp: number;
}

const messages = ref<ChatMessage[]>([]);
const isLoading = ref(false);
const error = ref<string | null>(null);

let messageCounter = 1;
let i18nReady = false;

const addMessage = (role: ChatMessage["role"], content: string): ChatMessage => {
  const msg: ChatMessage = {
    id: `msg-${messageCounter++}`,
    role,
    content,
    timestamp: Date.now(),
  };
  messages.value.push(msg);
  return msg;
};

const setWelcomeMessage = (content: string): void => {
  messages.value = [
    {
      id: "welcome",
      role: "assistant",
      content,
      timestamp: Date.now(),
    },
  ];
};

export const useChat = () => {
  const { t } = useI18n();

  if (!i18nReady) {
    setWelcomeMessage(t("chat.welcome"));
    i18nReady = true;
  }

  const sendMessage = async (text: string): Promise<void> => {
    const trimmed = text.trim();
    if (!trimmed || isLoading.value) return;

    addMessage("user", trimmed);
    isLoading.value = true;
    error.value = null;

    try {
      const history = messages.value
        .filter((m) => m.id !== "welcome" || messages.value.length > 2)
        .slice(-10)
        .map((m) => ({ role: m.role, content: m.content }));

      const response = await fetch("/api/chat", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ messages: history }),
      });

      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.error || `Request failed (${response.status})`);
      }

      const data = await response.json();
      addMessage("assistant", data.reply);
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Something went wrong";
      error.value = msg;
      addMessage("assistant", t("chat.error"));
    } finally {
      isLoading.value = false;
    }
  };

  const clearChat = (): void => {
    setWelcomeMessage(t("chat.cleared"));
    error.value = null;
  };

  return {
    messages,
    isLoading,
    error,
    sendMessage,
    clearChat,
    setWelcomeMessage,
  };
};
